import { apiClient, ApiResponse } from './client';

export type TimeRange = '7d' | '30d' | '90d' | '1y';
export type FloorPriceTimeRange = '7d' | '30d' | '90d' | '1y' | 'all';

export interface VolumeDataPoint {
  date: string; // ISO date
  volume: number; // Total volume in XRP
  sales: number; // Number of sales
  avgPrice: number; // Average sale price
}

export interface VolumeData {
  data: VolumeDataPoint[];
  totalVolume: number;
  totalSales: number;
  timeRange: TimeRange;
}

export interface CollectionStats {
  collectionId: string;
  totalVolume: number;
  totalSales: number;
  floorPrice: number | null;
  avgPrice: number;
  uniqueHolders: number;
  volumeChange24h: number; // Percentage
  floorPriceChange24h: number; // Percentage
  salesChange24h: number; // Percentage
}

/**
 * Fetch volume data for the marketplace or specific collection
 */
export async function getVolumeData(
  timeRange: TimeRange = '30d',
  collectionId?: string
): Promise<VolumeData> {
  try {
    const params = new URLSearchParams({ timeRange });
    if (collectionId) {
      params.append('collectionId', collectionId);
    }

    const response = await apiClient.get<ApiResponse<VolumeData>>(`/analytics/volume?${params.toString()}`);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch volume data');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching volume data:', error);
    throw error;
  }
}

/**
 * Fetch detailed statistics for a specific collection
 */
export async function getCollectionStats(collectionId: string): Promise<CollectionStats> {
  try {
    const response = await apiClient.get<ApiResponse<CollectionStats>>(`/analytics/collections/${collectionId}`);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch collection stats');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching collection stats:', error);
    throw error;
  }
}

/**
 * Fetch top collections by volume
 */
export async function getTopCollections(timeRange: TimeRange = '30d', limit: number = 10) {
  try {
    const params = new URLSearchParams({
      timeRange,
      limit: limit.toString(),
    });

    const response = await apiClient.get<ApiResponse<CollectionStats[]>>(`/analytics/top-collections?${params.toString()}`);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch top collections');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching top collections:', error);
    throw error;
  }
}

/**
 * Fetch marketplace-wide statistics
 */
export async function getMarketplaceStats(timeRange: TimeRange = '30d') {
  try {
    const params = new URLSearchParams({ timeRange });

    const response = await apiClient.get<ApiResponse<{
      totalVolume: number;
      totalSales: number;
      totalCollections: number;
      totalNFTs: number;
      activeListings: number;
      avgSalePrice: number;
      volumeChange: number;
      salesChange: number;
    }>>(`/analytics/marketplace?${params.toString()}`);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch marketplace stats');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching marketplace stats:', error);
    throw error;
  }
}

// Home page specific interfaces and functions

export interface LeaderboardCollection {
  rank: number;
  collectionId: string;
  name: string;
  slug?: string;
  imageUrl?: string;
  volume24h: string;
  volumeChange: number;
  floorPrice?: string;
  floorPriceChange: number;
  salesCount: number;
  ownersCount: number;
  verified: boolean;
  floor30d?: number[];
  volume30d?: number[];
}

export interface TrendingCollection {
  collectionId: string;
  name: string;
  slug?: string;
  imageUrl?: string;
  trendingScore: number;
  volumeIncrease: number;
  salesIncrease: number;
  uniqueBuyersIncrease: number;
  uniqueBuyers: number;
  currentVolume: string;
  currentSales: number;
  floorPrice?: string;
  verified: boolean;
  sales30d?: number[];
}

export interface MintLeaderboardCollection {
  rank: number;
  collectionId: string;
  name: string;
  imageUrl?: string;
  mintCount: number;
  verified: boolean;
}

export interface FloorPriceGainerCollection {
  rank: number;
  collectionId: string;
  name: string;
  slug?: string;
  imageUrl?: string;
  floorPriceChange: number; // Percentage change
  currentFloorPrice: number;
  previousFloorPrice: number;
  verified: boolean;
  floor30d?: number[];
}

export interface RecentlyAddedCollection {
  id: string;
  name: string;
  imageUrl?: string;
  creatorAddress: string;
  taxon: number;
  totalSupply: number;
  slug?: string;
  createdAt: string;
  verified: boolean;
}

/**
 * Fetch leaderboard (top volume/sales/floor collections)
 */
export async function getLeaderboard(
  period: '24h' | '7d' | '30d' | 'all' = '24h',
  metric: 'volume' | 'sales' | 'floor' = 'volume',
  limit: number = 10
): Promise<LeaderboardCollection[]> {
  try {
    const params = new URLSearchParams({
      period,
      metric,
      limit: limit.toString(),
    });

    const response = await apiClient.get<ApiResponse<{
      collections: LeaderboardCollection[];
      period: string;
      metric: string;
    }>>(`/analytics/leaderboard?${params.toString()}`);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch leaderboard');
    }

    return response.data.collections;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    throw error;
  }
}

/**
 * Fetch trending collections
 */
export async function getTrendingCollections(
  timeWindow: '1h' | '6h' | '24h' = '24h',
  limit: number = 10,
  verifiedOnly: boolean = false
): Promise<TrendingCollection[]> {
  try {
    const params = new URLSearchParams({
      timeWindow,
      limit: limit.toString(),
      verifiedOnly: verifiedOnly.toString(),
    });

    const response = await apiClient.get<ApiResponse<{
      collections: TrendingCollection[];
      timeWindow: string;
    }>>(`/analytics/trending?${params.toString()}`);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch trending collections');
    }

    return response.data.collections;
  } catch (error) {
    console.error('Error fetching trending collections:', error);
    throw error;
  }
}

/**
 * Fetch mint leaderboard (collections with most mints)
 */
export async function getMintLeaderboard(
  period: '24h' | '7d' | '30d' = '24h',
  limit: number = 10
): Promise<MintLeaderboardCollection[]> {
  try {
    const params = new URLSearchParams({
      period,
      limit: limit.toString(),
    });

    const response = await apiClient.get<ApiResponse<{
      collections: MintLeaderboardCollection[];
      period: string;
    }>>(`/analytics/mints?${params.toString()}`);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch mint leaderboard');
    }

    return response.data.collections;
  } catch (error) {
    console.error('Error fetching mint leaderboard:', error);
    throw error;
  }
}

/**
 * Fetch recently added collections
 */
export async function getRecentlyAddedCollections(
  limit: number = 10
): Promise<RecentlyAddedCollection[]> {
  try {
    const params = new URLSearchParams({
      sortBy: 'created',
      sortOrder: 'desc',
      limit: limit.toString(),
    });

    const response = await apiClient.get<ApiResponse<{
      collections: any[];
      total: number;
      page: number;
      limit: number;
    }>>(`/collections?${params.toString()}`);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch recently added collections');
    }

    // Map snake_case API response to camelCase interface
    return response.data.collections.map(c => ({
      id: c.id,
      name: c.name,
      imageUrl: c.image_url,
      creatorAddress: c.creator_address,
      taxon: c.taxon,
      totalSupply: c.total_supply,
      slug: c.slug,
      // Use indexed_at (when collection was added to platform) instead of created_at
      // Fall back to current time if both are null
      createdAt: c.indexed_at || c.created_at || new Date().toISOString(),
      verified: c.is_verified
    }));
  } catch (error) {
    console.error('Error fetching recently added collections:', error);
    throw error;
  }
}

/**
 * Unified home page data endpoint
 * Fetches all data needed for the home page in a single API call
 * Replaces the previous 4+ separate calls (leaderboard, trending, mints, recently added)
 */
export interface PromotedNft {
  nftTokenId: string;
  nftName: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  rarityRank: number | null;
  minSellPrice: number | null;
  ownerAddress: string | null;
  collectionId: string | null;
  collectionName: string | null;
  collectionSlug: string | null;
  collectionTotalSupply: number | null;
  expiresAt: string;
  tier: string;
}

export interface HomePageData {
  topVolume: LeaderboardCollection[];
  trending: TrendingCollection[];
  topGainers: FloorPriceGainerCollection[];
  topLosers: FloorPriceGainerCollection[]; // Same shape as gainers, just negative values
  featured: any | null; // Featured collection (first in DB for now)
  activePromotions: PromotedNft[];
}

export async function getHomePageData(): Promise<HomePageData> {
  try {
    const response = await apiClient.get<ApiResponse<any>>('/home');

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch home page data');
    }

    // Map the response data (API returns snake_case, we convert to camelCase)
    const data = response.data;

    return {
      topVolume: data.topVolume,
      trending: data.trending,
      topGainers: data.topGainers,
      topLosers: data.topLosers,
      featured: data.featured ? {
        collectionId: data.featured.id,
        name: data.featured.name,
        description: data.featured.description,
        imageUrl: data.featured.image_url,
        creatorAddress: data.featured.creator_address,
        taxon: data.featured.taxon,
        totalSupply: data.featured.total_supply,
        uniqueHolders: data.featured.unique_holders,
        slug: data.featured.slug,
        floorPrice: data.featured.floor_price,
        volume24h: data.featured.volume_24h,
        volume7d: data.featured.volume_7d,
        volumeAllTime: data.featured.volume_all_time,
        verified: data.featured.is_verified,
        previewNfts: data.featured.preview_nfts || [],
        previewMode: data.featured.preview_mode || 'cheapest',
      } : null,
      activePromotions: Array.isArray(data.activePromotions)
        ? data.activePromotions.map((p: any) => ({
            nftTokenId: p.nft_token_id,
            nftName: p.nft_name ?? null,
            imageUrl: p.image_url ?? null,
            thumbnailUrl: p.thumbnail_url ?? null,
            rarityRank: p.rarity_rank ?? null,
            minSellPrice: p.min_sell_price ?? null,
            ownerAddress: p.owner_address ?? null,
            collectionId: p.collection_id ?? null,
            collectionName: p.collection_name ?? null,
            collectionSlug: p.collection_slug ?? null,
            collectionTotalSupply: p.collection_total_supply ?? null,
            expiresAt: p.expires_at,
            tier: p.tier,
          }))
        : [],
    };
  } catch (error) {
    console.error('Error fetching home page data:', error);
    throw error;
  }
}

// Floor Price History interfaces and functions

export interface FloorPriceDataPoint {
  date: string; // ISO date (YYYY-MM-DD)
  floor_price: number; // Floor price in XRP
  source: 'offer' | 'sale'; // Source of the data point
}

export interface FloorPriceHistoryStats {
  total_entries: number;
  min_floor: number | null;
  max_floor: number | null;
  avg_floor: number | null;
  oldest_date: string | null;
  newest_date: string | null;
}

export interface FloorPriceHistoryData {
  history: FloorPriceDataPoint[];
  stats: FloorPriceHistoryStats;
}

// Combined Collection Analytics interfaces

export interface CollectionAnalyticsDataPoint {
  date: string; // ISO date (YYYY-MM-DD)
  floor_price: number | null; // Floor price in XRP
  volume: number | null; // Daily volume in XRP
  sales_count: number | null; // Number of sales for the day
}

export interface VolumeHistoryStats {
  total_entries: number;
  total_volume: number;
  total_sales: number;
  avg_daily_volume: number | null;
  max_daily_volume: number | null;
  oldest_date: string | null;
  newest_date: string | null;
}

export interface CollectionAnalyticsData {
  history: CollectionAnalyticsDataPoint[];
  stats: {
    floor_price: FloorPriceHistoryStats;
    volume: VolumeHistoryStats;
  };
}

/**
 * Convert time range to number of days
 */
function timeRangeToDays(timeRange: FloorPriceTimeRange): number | undefined {
  switch (timeRange) {
    case '7d': return 7;
    case '30d': return 30;
    case '90d': return 90;
    case '1y': return 365;
    case 'all': return undefined; // No limit
    default: return 90;
  }
}

// Rarity vs Price scatter plot interfaces

export interface RarityPriceDataPoint {
  nftTokenId: string;
  name: string | null;
  thumbnailUrl: string | null;
  rarityRank: number;
  rarityScore: number | null;
  price: number;
}

export interface RarityPriceData {
  dataPoints: RarityPriceDataPoint[];
  totalListed: number;
  totalWithRarity: number;
}

/**
 * Fetch rarity vs price data for listed NFTs in a collection
 * Used for scatter plot visualization to help users find underpriced rare NFTs
 */
export async function getRarityPriceData(collectionId: string): Promise<RarityPriceData> {
  try {
    const response = await apiClient.get<ApiResponse<RarityPriceData>>(
      `/collections/${collectionId}/rarity-price`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch rarity-price data');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching rarity-price data:', error);
    throw error;
  }
}

/**
 * Fetch combined analytics (floor price + volume) for a collection
 * Returns merged data by date for use in combined charts
 */
export async function getCollectionAnalytics(
  collectionId: string,
  timeRange: FloorPriceTimeRange = '90d'
): Promise<CollectionAnalyticsData> {
  try {
    const days = timeRangeToDays(timeRange);
    const params = new URLSearchParams();
    if (days !== undefined) {
      params.append('days', days.toString());
    }

    const url = `/collections/${collectionId}/analytics${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get<ApiResponse<CollectionAnalyticsData>>(url);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch collection analytics');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching collection analytics:', error);
    throw error;
  }
}
